import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { PacksContext } from '@/components/app/layout/PacksProvider';
import LoggedOut from '@/components/app/profile/LoggedOut';
import TabNavLinks from '@/components/app/ui/TabNavLinks';
import Loader from '@/components/Loader';
import { useAuth } from '@/context/auth/AuthProvider';
import { useUserState } from '@/hooks/store';
import { useRouterPush } from '@/hooks/utils/useRouterPush';

import { profileNavLinks } from './data';
import { ProfileLayoutProps } from './types';

const ProfileLayout = ({ children, hideNav }: ProfileLayoutProps) => {
	const { formatMessage } = useIntl();
	const user = useUserState();
	const router = useRouter();
	const pathname = usePathname();
	const { setPageTitle } = useContext(PacksContext);
	const routerPush = useRouterPush();
	const { isAuth0User } = useAuth();

	const [isRouting, setIsRouting] = useState(false);

	const getProfileNavLinks = () => {
		if (isAuth0User) {
			return profileNavLinks.filter(
				item => !['/app/profile/change-password', '/app/profile/update-user'].includes(item.href),
			);
		}

		return profileNavLinks.filter(item => !['/app/profile/account-settings'].includes(item.href));
	};

	const navItems = getProfileNavLinks().map(({ title, ...navItemContent }) => ({
		title: formatMessage(title),
		...navItemContent,
	}));

	const onItemClick = async (itemTitle: string) => {
		const nextItem = navItems.find(navItem => navItem.title === itemTitle);
		if (nextItem) {
			await routerPush(nextItem.href);
		}
	};

	const activeNavItem = navItems.find(navItem => pathname?.includes(navItem.href));

	useEffect(() => {
		if (activeNavItem) {
			setPageTitle(activeNavItem.title);
		}
	}, [setPageTitle, activeNavItem]);

	useEffect(() => {
		const enableLoader = () => setIsRouting(true);
		const disableLoader = () => setIsRouting(false);

		router.events.on('routeChangeStart', enableLoader);
		router.events.on('routeChangeComplete', disableLoader);

		return () => {
			router.events.off('routeChangeStart', enableLoader);
			router.events.off('routeChangeComplete', disableLoader);
		};
	}, [router.events]);

	const renderContent = () => {
		if (!user) {
			return <LoggedOut />;
		}

		return (
			<>
				{!hideNav && (
					<TabNavLinks navItems={navItems} activeNavItem={activeNavItem} onItemClick={onItemClick} />
				)}
				{isRouting ? <Loader /> : children}
			</>
		);
	};

	return renderContent();
};

export default ProfileLayout;
